import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, filter, of, switchMap, timer } from 'rxjs';
import { OKTA_AUTH } from '@okta/okta-angular';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { JobResponse, JobsApiService } from './jobs-api.service';

const POLL_INTERVAL_MS = 10_000;
const SSE_RECONNECT_DELAY_MS = 5_000;
const MAX_SSE_CONNECTIONS = 3;

@Injectable({ providedIn: 'root' })
export class JobPollingService {
  private readonly jobsApi = inject(JobsApiService);
  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly destroyRef = inject(DestroyRef);

  private readonly activeJobs = new Map<string, string>();
  private readonly jobStatusCallbacks = new Map<string, (job: JobResponse) => void>();
  private readonly eventSources = new Map<string, EventSource | EventSourcePolyfill>();
  private readonly sseJobIds = new Set<string>();
  private readonly acceptedRevisions = new Map<string, number>();
  private readonly reconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private pollingSubscription: { unsubscribe(): void } | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.pollingSubscription?.unsubscribe();
      for (const source of this.eventSources.values()) {
        source.close();
      }
      for (const reconnectTimer of this.reconnectTimers.values()) {
        clearTimeout(reconnectTimer);
      }
    });
  }

  registerJob(
    jobId: string,
    projectId: string,
    onUpdate: (job: JobResponse) => void,
  ): void {
    const alreadyActive = this.activeJobs.has(jobId);
    this.activeJobs.set(jobId, projectId);
    this.jobStatusCallbacks.set(projectId, onUpdate);
    if (alreadyActive) {
      return;
    }

    if (this.sseJobIds.size < MAX_SSE_CONNECTIONS) {
      this.sseJobIds.add(jobId);
      void this.startSSE(jobId, projectId, onUpdate);
    } else {
      this.ensurePolling();
    }
  }

  unregisterJob(jobId: string): void {
    const projectId = this.activeJobs.get(jobId);
    this.activeJobs.delete(jobId);
    this.sseJobIds.delete(jobId);
    this.acceptedRevisions.delete(jobId);

    if (projectId) {
      this.jobStatusCallbacks.delete(projectId);
    }

    this.eventSources.get(jobId)?.close();
    this.eventSources.delete(jobId);
    const reconnectTimer = this.reconnectTimers.get(jobId);
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      this.reconnectTimers.delete(jobId);
    }

    if (!this.hasPollingJobs()) {
      this.pollingSubscription?.unsubscribe();
      this.pollingSubscription = null;
    }
    this.promotePollingJobToSSE();
  }

  isPolling(jobId: string): boolean {
    return this.activeJobs.has(jobId);
  }

  private async startSSE(
    jobId: string,
    projectId: string,
    onUpdate: (job: JobResponse) => void,
  ): Promise<void> {
    try {
      const accessToken = await this.oktaAuth.getOrRenewAccessToken();
      if (!accessToken || !this.activeJobs.has(jobId)) {
        this.fallbackToPolling(jobId);
        return;
      }

      const eventSource = new EventSourcePolyfill(
        `/api/jobs/${encodeURIComponent(jobId)}/stream`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      eventSource.addEventListener('status', (event: any) => {
        try {
          const data = JSON.parse(event.data) as Partial<JobResponse>;
          const job: JobResponse = {
            jobId: data.jobId ?? jobId,
            projectId: data.projectId ?? projectId,
            jobType: data.jobType ?? 'provider_validation',
            status: data.status ?? 'running',
            progress: data.progress ?? null,
            schemaVersion: data.schemaVersion ?? 1,
            revision: data.revision ?? 0,
            progressDetail: data.progressDetail ?? null,
            createdAt: data.createdAt ?? '',
            startedAt: data.startedAt ?? null,
            completedAt: data.completedAt ?? null,
            ecsTaskArn: data.ecsTaskArn ?? null,
          };
          this.deliverIfCurrent(jobId, job, onUpdate);
        } catch (error) {
          console.error('Failed to parse validation status event.', error);
        }
      });
      eventSource.addEventListener('error', () => this.fallbackToPolling(jobId));
      this.eventSources.set(jobId, eventSource);
      if (!this.hasPollingJobs()) {
        this.pollingSubscription?.unsubscribe();
        this.pollingSubscription = null;
      }
    } catch (error) {
      console.warn('Live validation updates are unavailable; using polling.', error);
      this.fallbackToPolling(jobId);
    }
  }

  private deliverIfCurrent(
    jobId: string,
    job: JobResponse,
    onUpdate: (job: JobResponse) => void,
  ): void {
    const acceptedRevision = this.acceptedRevisions.get(jobId) ?? -1;
    if (job.revision < acceptedRevision) {
      return;
    }
    this.acceptedRevisions.set(jobId, job.revision);
    onUpdate(job);
    if (this.isTerminal(job)) {
      this.unregisterJob(jobId);
    }
  }

  private fallbackToPolling(jobId: string): void {
    this.eventSources.get(jobId)?.close();
    this.eventSources.delete(jobId);
    this.sseJobIds.delete(jobId);
    this.ensurePolling();

    if (!this.activeJobs.has(jobId) || this.reconnectTimers.has(jobId)) {
      return;
    }
    const reconnectTimer = setTimeout(() => {
      this.reconnectTimers.delete(jobId);
      const projectId = this.activeJobs.get(jobId);
      const callback = projectId ? this.jobStatusCallbacks.get(projectId) : undefined;
      if (!projectId || !callback || this.sseJobIds.size >= MAX_SSE_CONNECTIONS) {
        return;
      }
      this.sseJobIds.add(jobId);
      void this.startSSE(jobId, projectId, callback);
    }, SSE_RECONNECT_DELAY_MS);
    this.reconnectTimers.set(jobId, reconnectTimer);
  }

  private ensurePolling(): void {
    if (this.pollingSubscription || !this.hasPollingJobs()) {
      return;
    }
    this.pollingSubscription = timer(0, POLL_INTERVAL_MS)
      .pipe(
        filter(() => this.hasPollingJobs()),
        switchMap(() => {
          const polls = Array.from(this.activeJobs.entries())
            .filter(([jobId]) => !this.sseJobIds.has(jobId))
            .map(([jobId, projectId]) =>
              this.jobsApi
                .getJob(jobId)
                .pipe(catchError(() => of(null)))
                .toPromise()
                .then((job) => ({ jobId, projectId, job })),
            );
          return Promise.all(polls);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        for (const { jobId, projectId, job } of results) {
          const callback = this.jobStatusCallbacks.get(projectId);
          if (job && callback) {
            this.deliverIfCurrent(jobId, job, callback);
          }
        }
      });
  }

  private hasPollingJobs(): boolean {
    return Array.from(this.activeJobs.keys()).some((jobId) => !this.sseJobIds.has(jobId));
  }

  private promotePollingJobToSSE(): void {
    if (this.sseJobIds.size >= MAX_SSE_CONNECTIONS) {
      return;
    }
    const entry = Array.from(this.activeJobs.entries()).find(
      ([jobId]) => !this.sseJobIds.has(jobId),
    );
    if (!entry) {
      return;
    }
    const [jobId, projectId] = entry;
    const callback = this.jobStatusCallbacks.get(projectId);
    if (!callback) {
      return;
    }
    this.sseJobIds.add(jobId);
    void this.startSSE(jobId, projectId, callback);
  }

  private isTerminal(job: JobResponse): boolean {
    return job.status === 'done' || job.status === 'failed' || job.status === 'cancelled';
  }
}
